import useSWR from "swr";
import {
  getJobStatus,
  getSharedItemsBoardView,
  getSyncItemValues,
} from "../../utils/requests/getSharedItemsBoardView";
import { useState, useEffect, useCallback, useRef } from "react";
import { LinearProgressBar, Text } from "@vibe/core";
import { patchDiscrepanciesAfterResync } from "../../components/Resynchronization/optimisticUpdate";
import type { ApplyOptimisticUpdate } from "../../components/Resynchronization/resynchronization.interface";
import useDiscrepancyStream from "../services/useDiscrepancyStream";

const DISCREPANCY_PROGRESS_LABEL = "Analyzing items for discrepancies.";

interface ProcessingProgress {
  totalProcessed: number;
  totalItems: number;
  percentage: number;
}

const ProgressIndicator = ({ progress }: { progress: ProcessingProgress }) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [hideProgress, setHideProgress] = useState(false);

  useEffect(() => {
    if (progress.percentage !== 100) {
      setShowCompleted(false);
      setHideProgress(false);
      return;
    }

    setShowCompleted(true);
    const timer = setTimeout(() => {
      setHideProgress(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [progress.percentage]);

  if (hideProgress) return null;

  return (
    <div className="mb-4">
      <LinearProgressBar value={progress.percentage} />
      <div className="flex justify-between mt-1">
        <Text type="text2" color="secondary">
          {showCompleted ? "Completed" : `${progress.percentage}% ${DISCREPANCY_PROGRESS_LABEL}`}
        </Text>
        <Text type="text2" color="secondary">
          {progress.totalProcessed} of {progress.totalItems} items
        </Text>
      </div>
    </div>
  );
};

const NOT_FOUND_ERROR_MESSAGE = "Request failed with status code 404";

// No `jobs`: an empty board has nothing to scan, and a present `jobs` would restart the
// job machine in onSuccess.
const EMPTY_BOARD_VIEW_RESPONSE = {
  data: { data: { synced_items: [], cached: [], total: 0 } },
};

/**
 * The board-view request, with "this board has no synced items" answered as DATA.
 *
 * The backend reports an empty board as an error (utils-ms answers 404, which the backend
 * relays as a 500 carrying this message). Left as a rejection, SWR treats it like any other
 * failure and retries it on a backoff forever, and every retry flips `isValidating` and
 * `isLoading` back to true — so the page swapped its empty state for the tabbed view and back
 * again on each attempt. Resolving it here makes an empty board a settled answer: no retry,
 * nothing to flicker. Every other failure still rejects and reaches the error UI.
 */
const fetchBoardView = async (
  access_token: string,
  currentBoard: number,
  page: number,
  limit: number,
  filterQuery: string,
) => {
  try {
    return await getSharedItemsBoardView(access_token, currentBoard, page, limit, filterQuery);
  } catch (error: any) {
    if (error?.response?.data?.error_message === NOT_FOUND_ERROR_MESSAGE) {
      return EMPTY_BOARD_VIEW_RESPONSE as any;
    }
    throw error;
  }
};

const useBoardView = (
  currentBoard: number,
  access_token: string,
  userId: number,
  page: number,
  limit: number,
  filterQuery: string,
) => {
  const [error, setError] = useState(false);
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [syncedItems, setSyncedItems] = useState<any[]>([]);
  const [discrepancies, setDiscrepancies] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState(false);
  const [shouldShowProgressBar, setShouldShowProgressBar] = useState(false);
  const [hasCompletedDiscrepancyCache, setHasCompletedDiscrepancyCache] = useState(false);
  const [completedJobIds, setCompletedJobIds] = useState<Set<string>>(new Set());
  const completedJobIdsRef = useRef<Set<string>>(new Set());
  const hasCompletedDiscrepancyCacheRef = useRef(false);
  const [progress, setProgress] = useState<ProcessingProgress>({
    totalProcessed: 0,
    totalItems: 0,
    percentage: 0,
  });
  const previousFilterQuery = useRef(filterQuery);

  const getCurrentBoardItemIds = useCallback(
    (items: any[] = []) =>
      Array.from(
        new Set(
          items.flatMap((syncedItem: any) =>
            (syncedItem.items ?? [])
              .filter((item: any) => String(item.board_id ?? item.boardid) === String(currentBoard))
              .map((item: any) => String(item.item_id ?? item.itemid))
              .filter((itemId: string) => itemId !== "undefined"),
          ),
        ),
      ),
    [currentBoard],
  );

  const reset = useCallback(() => {
    setError(false);
    setIsProcessing(false);
    setIsBackgroundProcessing(false);
    setShouldShowProgressBar(false);
    setJobIds([]);
    setHasCompletedDiscrepancyCache(false);
    hasCompletedDiscrepancyCacheRef.current = false;
    completedJobIdsRef.current = new Set();
    setCompletedJobIds(new Set());
    setSyncedItems([]);
    setDiscrepancies([]);
    setProgress({
      totalProcessed: 0,
      totalItems: 0,
      percentage: 0,
    });
  }, []);

  const startDiscrepancyJobs = useCallback((jobs: any[], shouldShowVisibleProgress: boolean) => {
    const nextJobIds = jobs.map((job: any) => job.jobId);

    setJobIds(nextJobIds);
    setIsProcessing(shouldShowVisibleProgress && nextJobIds.length > 0);
    setIsBackgroundProcessing(!shouldShowVisibleProgress && nextJobIds.length > 0);
    setShouldShowProgressBar(shouldShowVisibleProgress && nextJobIds.length > 0);

    if (shouldShowVisibleProgress) {
      setHasCompletedDiscrepancyCache(false);
      hasCompletedDiscrepancyCacheRef.current = false;
    }

    completedJobIdsRef.current = new Set();
    setCompletedJobIds(new Set());

    const totalItems = jobs.reduce((acc: number, job: any) => {
      return acc + (job.progress?.totalCount || 0);
    }, 0);

    setProgress({
      totalProcessed: 0,
      totalItems,
      percentage: 0,
    });
  }, []);

  // Initial request to get job IDs
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: initialData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: initialError,
    mutate: revalidateDiscrepancies,
    isLoading: isInitialLoading,
    isValidating: isInitialValidating,
  } = useSWR(
    access_token &&
      userId &&
      currentBoard &&
      filterQuery === "discrepancies" &&
      !hasCompletedDiscrepancyCache
      ? `board_view_init:${userId}-${currentBoard}-${filterQuery}`
      : null,
    () => fetchBoardView(access_token, currentBoard, page, limit, filterQuery),
    {
      revalidateOnFocus: false,
      onSuccess: (data) => {
        const jobs = data?.data?.data?.jobs;
        const cached = data?.data?.data?.cached ?? [];

        if (cached.length > 0) {
          setSyncedItems((prev) => {
            const map = new Map(prev.map((item: any) => [item._id, item]));
            cached.forEach((flags: any) => {
              map.set(flags.syncId, {
                _id: flags.syncId,
                items: (flags.board_names ?? []).map((b: any) => ({
                  item_id: b.item_id,
                  board_id: b.board_id,
                  board_name: b.board_name,
                })),
              });
            });
            return Array.from(map.values());
          });
          setDiscrepancies((prev) => {
            const map = new Map(prev.map((item: any) => [item.syncId, item]));
            cached.forEach((flags: any) => map.set(flags.syncId, flags));
            return Array.from(map.values());
          });
        }

        if (jobs) {
          startDiscrepancyJobs(jobs, true);
        }
      },
      // An empty board never gets here — fetchBoardView resolves it.
      onError: () => setError(true),
    },
  );

  useEffect(() => {
    const wasDiscrepanciesFilter = previousFilterQuery.current === "discrepancies";
    previousFilterQuery.current = filterQuery;

    if (
      filterQuery !== "discrepancies" ||
      wasDiscrepanciesFilter ||
      !hasCompletedDiscrepancyCache
    ) {
      return;
    }

    getSharedItemsBoardView(access_token, currentBoard, page, limit, filterQuery)
      .then((data) => {
        const jobs = data?.data?.data?.jobs;
        const cached = data?.data?.data?.cached ?? [];

        if (cached.length > 0) {
          setSyncedItems((prev) => {
            const map = new Map(prev.map((item: any) => [item._id, item]));
            cached.forEach((flags: any) => {
              map.set(flags.syncId, {
                _id: flags.syncId,
                items: (flags.board_names ?? []).map((b: any) => ({
                  item_id: b.item_id,
                  board_id: b.board_id,
                  board_name: b.board_name,
                })),
              });
            });
            return Array.from(map.values());
          });
          setDiscrepancies((prev) => {
            const map = new Map(prev.map((item: any) => [item.syncId, item]));
            cached.forEach((flags: any) => map.set(flags.syncId, flags));
            return Array.from(map.values());
          });
        }

        if (jobs) {
          startDiscrepancyJobs(jobs, false);
        }
      })
      .catch(() => {
        setIsBackgroundProcessing(false);
      });
  }, [
    access_token,
    currentBoard,
    filterQuery,
    hasCompletedDiscrepancyCache,
    limit,
    page,
    startDiscrepancyJobs,
  ]);

  // Stream-driven completion: each socket chunk merges its result and marks its
  // job complete; when all jobs are done the run ends. Replaces the old polling
  // fan-out. Redis job-status writes + getJobStatus remain as the reconnect
  // fallback (see onReconnect below).
  const handleStreamChunk = useCallback(
    (data: { jobId: string; syncIds: string[]; result: any }) => {
      // Dedupe by jobId: the connect catch-up sweep and a late live emit can
      // both deliver the same job. Merges are idempotent (Map-keyed) but the
      // progress increment is not, so a second delivery would double-count.
      if (completedJobIdsRef.current.has(data.jobId)) return;

      const result = data.result ?? {};

      setSyncedItems((prev) => {
        const map = new Map(prev.map((item: any) => [item._id, item]));
        (result.synced_items || []).forEach((item: any) => map.set(item._id, item));
        return Array.from(map.values());
      });
      setDiscrepancies((prev) => {
        const map = new Map(prev.map((item: any) => [item.syncId, item]));
        (result.discrepancies || []).forEach((item: any) => map.set(item.syncId, item));
        return Array.from(map.values());
      });

      setCompletedJobIds((prev) => {
        const next = new Set([...prev, data.jobId]);
        completedJobIdsRef.current = next;

        setProgress((p) => {
          const totalProcessed = p.totalProcessed + (data.syncIds?.length ?? 0);
          const percentage = p.totalItems
            ? Math.min(Math.round((totalProcessed / p.totalItems) * 100), 100)
            : 100;
          return { ...p, totalProcessed, percentage };
        });

        if (next.size === jobIds.length) {
          setIsProcessing(false);
          setIsBackgroundProcessing(false);
          // hasCompletedDiscrepancyCache gates the Discrepancies init fetch — only
          // set it when actually in the Discrepancies view, or All-items completion
          // would silently suppress a later Discrepancies load.
          if (filterQuery === "discrepancies") {
            setHasCompletedDiscrepancyCache(true);
            hasCompletedDiscrepancyCacheRef.current = true;
          }
          setProgress((p) => ({ ...p, percentage: 100 }));
        }

        return next;
      });
    },
    [jobIds, filterQuery],
  );

  const handleStreamFailure = useCallback(() => {
    if (!isBackgroundProcessing) setError(true);
    setIsProcessing(false);
    setIsBackgroundProcessing(false);
  }, [isBackgroundProcessing]);

  // Catch-up reconciliation: room emits are not buffered, so a chunk that
  // finished before this client joined the room (or during a disconnect) is
  // lost. On (re)connect, sweep getJobStatus once for every in-flight job and
  // replay any already-completed results through the same merge path. Uses the
  // retained discrepancy_job: Redis fallback — no new persistence.
  const reconcileInFlightJobs = useCallback(async () => {
    const pending = jobIds.filter((jobId) => !completedJobIdsRef.current.has(jobId));

    await Promise.all(
      pending.map(async (jobId) => {
        try {
          const response = await getJobStatus(access_token, jobId);

          if (response?.data?.status === "completed") {
            handleStreamChunk({
              jobId,
              syncIds: response.data.syncIds ?? [],
              result: response.data.result,
            });
          } else if (response?.data?.status === "failed") {
            handleStreamFailure();
          }
        } catch {
          // Best-effort: a failed status read should not break the live stream.
        }
      }),
    );
  }, [jobIds, access_token, handleStreamChunk, handleStreamFailure]);

  useDiscrepancyStream({
    userId,
    onChunk: handleStreamChunk,
    onFailure: handleStreamFailure,
    // Reconcile on both initial connect (the common emit-before-subscribe race)
    // and reconnect (events missed while disconnected).
    onConnect: () => reconcileInFlightJobs(),
    onReconnect: () => reconcileInFlightJobs(),
    enabled: isProcessing || isBackgroundProcessing,
  });

  // For non-discrepancy queries, use the original behavior
  const {
    data: regularData,
    isLoading,
    isValidating,
    mutate: revalidateAllItems,
  } = useSWR(
    access_token && userId && currentBoard && filterQuery !== "discrepancies"
      ? `board_view:${userId}-${currentBoard}-${page}-${limit}-${filterQuery}`
      : null,
    () => fetchBoardView(access_token, currentBoard, page, limit, filterQuery),
    {
      revalidateOnFocus: false,
      // Retain the previous page's data (incl. `total`) while the next page loads,
      // so server-side pagination doesn't flicker `total` to 0 and blank the
      // MUI page-number buttons on every page change.
      keepPreviousData: true,
      onSuccess: (data) => {
        // Seed this page's cached verdicts into the badge state; streamed results
        // merge in later. The row LIST stays SWR-driven (server-paginated) — only
        // badge data accumulates here.
        const cached = data?.data?.data?.cached ?? [];
        const jobs = data?.data?.data?.jobs;

        if (cached.length > 0) {
          setDiscrepancies((prev) => {
            const map = new Map(prev.map((item: any) => [item.syncId, item]));
            cached.forEach((flags: any) => map.set(flags.syncId, flags));
            return Array.from(map.values());
          });
        }

        if (jobs) {
          // Background mode: per-badge loading, no full-width progress bar.
          startDiscrepancyJobs(jobs, false);
        }
      },
      // An empty board never gets here — fetchBoardView resolves it.
      onError: () => setError(true),
    },
  );

  /**
   * Fetches one syncId's live column values for an expanding row — and adopts the
   * verdict that arrives with them.
   *
   * `/simb/sync-item-values` recomputes the WHOLE result (getSharedColumns →
   * getColumnValues → findDiscrepancies), so its `shared_columns[].discrepancy` and
   * `total_discrepancies` are computed against the very values it is returning. The row
   * being replaced came from `disc:flags:<syncId>`, which the board view serves for up
   * to 600s and which nothing invalidates when a column changes on Monday. Keeping the
   * cached verdict next to live values is what let a row show Stuck on one board and
   * Done on the other under a pill reading "in sync", with Sync Latest disabled.
   *
   * Merged here rather than in ExpandedRow because every stale surface reads THIS array:
   * the row's issue chip (ItemsTable), each column's state pill and Sync Latest's
   * enablement (sharedColumnsAdapter). One merge corrects all three.
   *
   * Guarded on `result?.syncId` — getSyncItemValues answers null on a 404, and keying
   * that into the map would add a phantom row rather than replace anything.
   */
  const fetchSyncItemValues = useCallback(
    async (syncId: string) => {
      const result = await getSyncItemValues(access_token, syncId);

      if (result?.syncId) {
        setDiscrepancies((prev) => {
          const map = new Map(prev.map((entry: any) => [entry.syncId, entry]));
          map.set(result.syncId, result);
          return Array.from(map.values());
        });
      }

      return result;
    },
    [access_token],
  );

  const applyOptimisticUpdate: ApplyOptimisticUpdate = useCallback(
    (syncId, targetItems, newValue, newText, columnType, columnTitle, columnHasDiscrepancy) => {
      const args = {
        syncId,
        targetItems,
        newValue,
        newText,
        columnType,
        columnTitle,
        columnHasDiscrepancy,
      };
      setDiscrepancies((prev) => patchDiscrepanciesAfterResync(prev, args));
    },
    [],
  );

  if (filterQuery === "discrepancies") {
    const shouldShowInitialLoading = isInitialLoading && completedJobIds.size === 0;

    return {
      isLoading: shouldShowInitialLoading,
      error,
      syncedItems,
      syncedBoardItemIds: getCurrentBoardItemIds(syncedItems),
      discrepancies,
      total: syncedItems.length,
      isValidating: isInitialValidating,
      mutate: revalidateDiscrepancies,
      applyOptimisticUpdate,
      fetchSyncItemValues,
      progress: progress.percentage,
      progressBar:
        isProcessing && shouldShowProgressBar ? <ProgressIndicator progress={progress} /> : null,
      isProcessing: isProcessing,
      reset,
    };
  }

  return {
    isLoading,
    error,
    syncedItems: regularData?.data?.data?.synced_items || [],
    syncedBoardItemIds:
      regularData?.data?.data?.synced_board_item_ids?.map((itemId: any) => String(itemId)) ||
      getCurrentBoardItemIds(regularData?.data?.data?.synced_items || []),
    discrepancies,
    total: regularData?.data?.data?.total || 0,
    isValidating,
    mutate: revalidateAllItems,
    applyOptimisticUpdate,
    fetchSyncItemValues,
    progress: 0,
    progressBar: null,
    reset,
  };
};

export default useBoardView;
